import { TemplateResult, html, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import { dateShort } from '@trp-dms-digital-shared/ds-ui-library-common';

import { PopBaseTile } from '../../base/pop-beacon/beacon-tile.js';
import { Constants, Selectors, Text } from './product-tile-ui.constants.js';
import type { ProductTileUiContentObject } from './product-tile-ui.model.js';
import { IconSVG } from '../../common/utils/icon.js';
import style from './product-tile-ui.scss?inline';
import { formatDecimalAsPercentage } from '../../common/utils/formatNumber';

/**
 * A UI that represents on ETF in a BeaconTile format
 * @tag pop-product-tile-ui-component
 * @attribute { ProductTileUiContentObject } [data-component-object] ProductTileUi content object
 *
 * not correctly typed, will be fixed within base-component work
 * @rally https://rally1.rallydev.com/#/?detail=/userstory/816362881827&fdp=true
 */
@customElement(Constants.TAG_NAME)
export class PopProductTileUiComponent extends PopBaseTile {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }
  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * ProductTileUi content object
   */
  componentObject?: ProductTileUiContentObject;

  /**
   * Gets the url
   * (this may change as we get more requirements for URL)
   */
  getUrl() {
    if (!this.componentObject?.ticker || !this.componentObject?.urlRoot) {
      console.error('ticker or urlRoot missing from componentObject');
      return '';
    }
    return `${this.componentObject?.urlRoot}/${this.componentObject?.ticker}`;
  }

  renderTicker() {
    return html`<div
      class="${Selectors.TICKER} b-typography-sans-serif--caption--100 b-font-weight-bold"
    >
      ${this.componentObject?.ticker}
    </div>`;
  }

  renderAsOfDate() {
    if (this.componentObject?.price?.asOfDate) {
      const asOfDate = this.componentObject?.price?.asOfDate;
      const timeElement = html`<time datetime="${asOfDate}"
        >${dateShort(asOfDate)}</time
      >`;
      return html`<div
        class="${Selectors.AS_OF_DATE} b-typography-sans-serif--caption--100"
      >
        ${Text.AS_OF} ${timeElement}
      </div>`;
    }

    return html``;
  }

  /**
   * Renders NAME, binds the last word and the SVG together
   */
  renderName() {
    const url = this.getUrl();
    const parts = this.componentObject?.shortName?.split(' ');
    const lastWord = parts?.pop();

    return html`<div
      class="${Selectors.NAME} b-padding-block-start--micro--normal"
    >
      <a href="${url}" class="b-typography-serif--headline--50"
        >${parts?.join(' ')}
        <span class="last-word"
          >${lastWord} ${this.getSemiTransparentProductSuffix()}
          ${IconSVG.chevronRight()}</span
        ></a
      >
    </div>`;
  }

  /**
   * Renders one body term/definition
   * @param classes - term-specific classes
   * @param term
   * @param desc
   */
  renderItem(classes, term, desc): TemplateResult {
    return html`
      <div class="${classes}">
        <dt class="b-typography-sans-serif--caption--100 b-font-weight-bold">
          ${term}
        </dt>
        <dd class="b-typography-sans-serif--caption--100">${desc}</dd>
      </div>
    `;
  }

  /**
   * Adds a currency symbol as a suffix
   * @rally (POP internationalization) https://rally1.rallydev.com/#/?detail=/userstory/819561116563&fdp=true
   */
  getTextCurrencySuffix(content) {
    // currency should not be hard-coded
    return html`${content} ($)`;
  }

  getSemiTransparentProductSuffix(): string {
    return this.componentObject?.transparencyTypeName === 'Semi-Transparent'
      ? html`<sup>*</sup>`
      : ``;
  }

  /**
   * beacon-tile method override
   */
  renderHeader() {
    return html`<div class="b-flex b-justify-between">
        ${this.renderTicker()} ${this.renderAsOfDate()}
      </div>
      ${this.renderName()}`;
  }

  /**
   * beacon-tile method override
   */
  renderBody() {
    const items: TemplateResult[] = [];

    // Category
    if (this.componentObject?.assetClassName) {
      items.push(
        this.renderItem(
          Selectors.CATEGORY,
          Text.CATEGORY,
          this.componentObject.assetClassName,
        ),
      );
    }

    // Expense Ratio
    if (this.componentObject?.grossExpenseRatio) {
      items.push(
        this.renderItem(
          Selectors.EXPENSE_RATIO,
          Text.EXPENSE_RATIO,
          formatDecimalAsPercentage(this.componentObject.grossExpenseRatio, 2),
        ),
      );
    }

    // NAV
    if (this.componentObject?.price?.navPrice) {
      items.push(
        this.renderItem(
          Selectors.NAV,
          this.getTextCurrencySuffix(Text.NAV),
          this.componentObject.price.navPrice,
        ),
      );
    }

    // Market Price
    if (this.componentObject?.price?.navPrice) {
      items.push(
        this.renderItem(
          Selectors.MARKET_PRICE,
          this.getTextCurrencySuffix(Text.MARKET_PRICE),
          this.componentObject.price.navPrice,
        ),
      );
    }

    if (items.length) {
      return html`<dl class="${Selectors.BODY} b-flex b-justify-start">
        ${items}
      </dl>`;
    }

    return html``;
  }

  renderContentZone() {
    return html`<div class="content-zone b-padding--cell-contained">
      ${this.renderHeader()} ${this.renderBody()} ${this.renderFooter()}
    </div>`;
  }

  renderTile() {
    return html`<div class="container b-padding--organism-normal">
      ${this.renderContentZone()} ${this.renderMedia()}
    </div>`;
  }

  render() {
    return this.renderTile();
  }
}
